import type { UrlObject } from 'url';

import {
  BASE_CAREER_URL,
  BASE_HOW_TO_CHANGE_STRAP_URL,
  BASE_MALLELONDON_URL,
  BASE_PRESS_LOUNGE_URL,
  BASE_STORE_FINDER_URL,
} from '@/lib/const';

export interface AriaLabelDictionary {
  actions: Record<string, string>;
  constants: Record<string, string>;
  forms: Record<string, string>;
  urls: Record<string, string>;
}

const ariaLabelCache = new Map<string, AriaLabelDictionary>();

export function clearAriaLabelCache(): void {
  ariaLabelCache.clear();
}

export async function loadAriaLabels(
  languageCode: string,
): Promise<AriaLabelDictionary> {
  // Normalize the language code for consistent cache keys
  const normalizedLangCode = languageCode.toUpperCase().replace('-', '_');

  if (ariaLabelCache.has(normalizedLangCode)) {
    return ariaLabelCache.get(normalizedLangCode)!;
  }

  try {
    let ariaLabels: AriaLabelDictionary;

    switch (normalizedLangCode) {
      case 'EN_US':
        ariaLabels = (await import('@/locale/aria-labels/en-US.json')).default;
        break;
      case 'EN_GB':
        ariaLabels = (await import('@/locale/aria-labels/en-GB.json')).default;
        break;
      case 'DE':
        ariaLabels = (await import('@/locale/aria-labels/de.json')).default;
        break;
      case 'FR':
        ariaLabels = (await import('@/locale/aria-labels/fr.json')).default;
        break;
      case 'ES':
        ariaLabels = (await import('@/locale/aria-labels/es.json')).default;
        break;
      case 'IT':
        ariaLabels = (await import('@/locale/aria-labels/it.json')).default;
        break;
      case 'JA':
        ariaLabels = (await import('@/locale/aria-labels/ja.json')).default;
        break;
      case 'KO':
        ariaLabels = (await import('@/locale/aria-labels/ko.json')).default;
        break;
      case 'PL':
        ariaLabels = (await import('@/locale/aria-labels/pl.json')).default;
        break;
      case 'PT':
        ariaLabels = (await import('@/locale/aria-labels/pt.json')).default;
        break;
      case 'RU':
        ariaLabels = (await import('@/locale/aria-labels/ru.json')).default;
        break;
      case 'ZH':
      case 'ZH_HANS':
        ariaLabels = (await import('@/locale/aria-labels/zh.json')).default;
        break;
      case 'ZH_HANT':
        ariaLabels = (await import('@/locale/aria-labels/zh-Hant.json'))
          .default;
        break;
      default:
        ariaLabels = (await import('@/locale/aria-labels/en-US.json')).default;
        break;
    }

    if (
      ariaLabels &&
      typeof ariaLabels === 'object' &&
      'default' in ariaLabels &&
      !ariaLabels.urls
    ) {
      ariaLabels = (ariaLabels as any).default;
    }

    ariaLabelCache.set(normalizedLangCode, ariaLabels);
    return ariaLabels;
  } catch (error) {
    console.warn(
      `Failed to load aria-labels for ${languageCode}, falling back to en-US`,
      error,
    );
    const fallback = (await import('@/locale/aria-labels/en-US.json')).default;
    ariaLabelCache.set(normalizedLangCode, fallback);
    return fallback;
  }
}

/**
 * Generate a human-readable fallback aria-label from a URL path.
 * Matches the naming conventions used in the aria-label dictionaries:
 * - Collections: "{Name} collection" (e.g., "Navitimer collection")
 * - Watches: Descriptive with "watches" suffix (e.g., "Aviation watches")
 * - Straps: "{Type} straps" (e.g., "Leather straps")
 * - Service: Descriptive (e.g., "Breitling services")
 * - About: Descriptive (e.g., "About Breitling")
 */
function generateFallbackAriaLabel(normalizedUrl: string): string {
  // Remove leading/trailing slashes and locale prefix
  let path = normalizedUrl.replace(/^\//, '').replace(/\/$/, '');

  // Remove locale prefix (e.g., 'us-en/', 'de-de/')
  path = path.replace(/^[a-z]{2}-[a-z]{2}\//i, '');

  // If path is empty, it's the home page
  if (!path) {
    return 'Breitling home page';
  }

  // Get the segments of the path
  const segments = path.split('/').filter(Boolean);
  const firstSegment = segments[0] || '';
  const lastSegment = segments[segments.length - 1] || '';

  // Convert kebab-case to Title Case
  const toTitleCase = (str: string): string => {
    return str
      .split('-')
      .map((word) => {
        // Handle common abbreviations
        const upperWords = ['nfl', 'faq', 'gmt', 'avi', 'usa', 'uk', 'gb'];
        if (upperWords.includes(word.toLowerCase())) {
          return word.toUpperCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  const humanReadable = toTitleCase(lastSegment);

  // Build labels that match dictionary naming conventions
  if (firstSegment === 'collections') {
    // Dictionary pattern: "{Name} collection" (e.g., "Navitimer collection")
    if (lastSegment === 'collections' || !lastSegment) {
      return 'All watch collections';
    }
    return `${humanReadable} collection`;
  }

  if (firstSegment === 'watches') {
    // Dictionary pattern: Descriptive with context (e.g., "Aviation watches", "All watches")
    if (lastSegment === 'watches' || !lastSegment) {
      return 'Browse all watches';
    }
    if (lastSegment === 'all') {
      return 'All watches';
    }
    if (lastSegment === 'men' || lastSegment === 'mens-watches') {
      return 'Mens watches';
    }
    if (lastSegment === 'women' || lastSegment === 'womens-watches') {
      return 'Womens watches';
    }
    if (lastSegment === 'novelties') {
      return 'New watches';
    }
    if (lastSegment === 'limited-editions') {
      return 'Limited edition watches';
    }
    // Specific mappings to match dictionary entries exactly
    if (lastSegment === 'nfl-collection') {
      return 'NFL Team Edition watches';
    }
    if (lastSegment === 'material-gold-watches') {
      return 'Gold watches';
    }
    if (lastSegment === 'style-manufacture') {
      return 'Manufacture watches';
    }
    // For category pages like /watches/air, /watches/land, /watches/sea
    if (['air', 'land', 'sea'].includes(lastSegment)) {
      const categoryNames: Record<string, string> = {
        air: 'Aviation watches',
        land: 'Land sports watches',
        sea: 'Diving watches',
      };
      return categoryNames[lastSegment] || `${humanReadable} watches`;
    }
    // Default: "{Name} watches"
    return `${humanReadable} watches`;
  }

  if (firstSegment === 'straps') {
    // Dictionary pattern: "{Type} straps" (e.g., "Leather straps", "Rubber straps")
    if (lastSegment === 'straps' || !lastSegment) {
      return 'Browse watch straps';
    }
    if (lastSegment === 'all') {
      return 'All watch straps';
    }
    if (lastSegment === 'change-watch-strap') {
      return 'How to change your watch strap';
    }
    // Specific mappings to match dictionary entries exactly
    if (lastSegment === 'alligator') {
      return 'Alligator leather straps';
    }
    // Default: "{Type} straps"
    return `${humanReadable} straps`;
  }

  if (firstSegment === 'service') {
    // Dictionary pattern: Descriptive (e.g., "Breitling services", "Service pricing")
    if (lastSegment === 'service' || !lastSegment) {
      return 'Breitling services';
    }
    if (lastSegment === 'contact') {
      return 'Contact customer service';
    }
    if (lastSegment === 'faq') {
      return 'Frequently asked questions';
    }
    if (lastSegment === 'my-watch-needs-a-service') {
      return 'Request watch service';
    }
    if (lastSegment === 'our-services') {
      return 'Our after-sales services';
    }
    if (lastSegment === 'service-prices') {
      return 'Service pricing';
    }
    if (lastSegment === 'warranties') {
      return 'International warranty';
    }
    // Default: Use title case
    return humanReadable;
  }

  if (firstSegment === 'about') {
    // Dictionary pattern: Descriptive (e.g., "About Breitling", "Breitling history")
    if (lastSegment === 'about' || !lastSegment) {
      return 'About Breitling';
    }
    if (lastSegment === 'history') {
      return 'Breitling history';
    }
    if (lastSegment === 'partnerships') {
      return 'Breitling partnerships';
    }
    if (lastSegment === 'ambassadors') {
      return 'View all Breitling ambassadors';
    }
    // Specific mappings to match dictionary entries exactly
    if (lastSegment === 'origins-label') {
      return 'The Origins Label traceability program';
    }
    // For ambassador pages
    if (segments.includes('ambassadors') && segments.length > 2) {
      return `Ambassador ${humanReadable}`;
    }
    // Default: Use title case
    return humanReadable;
  }

  if (firstSegment === 'my-account') {
    // Dictionary pattern: Descriptive (e.g., "My account", "View your order history")
    if (lastSegment === 'my-account' || !lastSegment) {
      return 'My account';
    }
    if (lastSegment === 'login') {
      return 'Sign in to your account';
    }
    if (lastSegment === 'my-orders') {
      return 'View your order history';
    }
    if (lastSegment === 'my-watches') {
      return 'Register your Breitling watch';
    }
    if (lastSegment === 'my-wishlist') {
      return 'View your wishlist';
    }
    if (lastSegment === 'my-addresses') {
      return 'Manage your addresses';
    }
    if (lastSegment === 'my-details') {
      return 'View and edit account details';
    }
    if (lastSegment === 'forgotten-password') {
      return 'Reset your password';
    }
    return humanReadable;
  }

  if (firstSegment === 'stores') {
    return 'Find a boutique near you';
  }

  if (firstSegment === 'privacy-policy') {
    return 'Privacy policy';
  }

  if (firstSegment === 'terms-and-conditions') {
    return 'Terms and conditions';
  }

  if (firstSegment === 'terms-of-use') {
    return 'Terms of use';
  }

  if (firstSegment === 'campaigns') {
    // Dictionary pattern: Descriptive (e.g., "Breitling books")
    if (lastSegment === 'books') {
      return 'Breitling books';
    }
    return humanReadable;
  }

  // Default fallback - use simple title case
  return humanReadable;
}

function normalizeUrl(url: UrlObject | string): string {
  let normalizedUrl: string;

  if (typeof url === 'object' && url !== null) {
    // Manual URL formatting to avoid Node.js 'url' module in Edge Runtime
    let pathname = url.pathname || '';
    if (url.query) {
      pathname = Object.entries(url.query).reduce(
        (path, [key, value]) => path?.replace(`[${key}]`, value as string),
        pathname,
      );
    }
    normalizedUrl = pathname;
  } else {
    normalizedUrl = url || '';
  }

  const urlSplits = normalizedUrl.split('?');
  let cleanUrl = urlSplits[0];

  if (cleanUrl.startsWith('http')) {
    cleanUrl = cleanUrl.split('/').slice(3).join('/');
    cleanUrl = '/' + cleanUrl;
  }

  cleanUrl = cleanUrl.endsWith('/') ? cleanUrl : cleanUrl + '/';

  cleanUrl = cleanUrl.startsWith('/') ? cleanUrl : '/' + cleanUrl;

  // Remove locale prefix (e.g., '/us-en/', '/de-de/') to match dictionary keys
  cleanUrl = cleanUrl.replace(/^\/[a-z]{2}-[a-z]{2}\//i, '/');

  return cleanUrl.toLowerCase();
}

export async function getAriaLabel(
  url: UrlObject | string,
  languageCode: string,
): Promise<string | undefined> {
  if (!url) return undefined;

  const ariaLabels = await loadAriaLabels(languageCode);

  if (typeof url === 'string') {
    const formattedUrl = url.endsWith('/') ? url : url + '/';

    switch (formattedUrl) {
      case BASE_STORE_FINDER_URL:
        return ariaLabels.constants.discover_store;
      case BASE_HOW_TO_CHANGE_STRAP_URL:
        return ariaLabels.constants.learn_how_to_change_strap;
      case BASE_CAREER_URL:
        return ariaLabels.constants.career;
      case BASE_PRESS_LOUNGE_URL:
        return ariaLabels.constants.press_lounge;
      case BASE_MALLELONDON_URL:
        return ariaLabels.constants.mallelondon_collection;
    }
  }

  const normalizedUrl = normalizeUrl(url);
  const ariaLabel = ariaLabels.urls[normalizedUrl];

  if (ariaLabel) {
    return ariaLabel;
  }

  // If no aria-label found in dictionary, return undefined
  // This forces developers to add missing URLs to the dictionary
  return undefined;
}

export async function getFormAriaLabel(
  fieldKey: string,
  languageCode: string,
): Promise<string | undefined> {
  const ariaLabels = await loadAriaLabels(languageCode);
  return ariaLabels.forms[fieldKey];
}

export async function getActionAriaLabel(
  actionKey: string,
  languageCode: string,
): Promise<string | undefined> {
  const ariaLabels = await loadAriaLabels(languageCode);
  return ariaLabels.actions[actionKey];
}

export function getAriaLabelSync(
  url: UrlObject | string,
  languageCode: string,
): string | undefined {
  if (!url) return undefined;

  const normalizedUrl = normalizeUrl(url);

  // Guard against missing language code
  if (!languageCode) {
    return generateFallbackAriaLabel(normalizedUrl);
  }

  // Normalize the language code to match cache keys
  const normalizedLangCode = languageCode.toUpperCase().replace('-', '_');
  const cachedAriaLabels = ariaLabelCache.get(normalizedLangCode);

  // If cache is not loaded yet, generate a fallback label
  if (!cachedAriaLabels) {
    return generateFallbackAriaLabel(normalizedUrl);
  }

  if (typeof url === 'string') {
    const formattedUrl = url.endsWith('/') ? url : url + '/';

    switch (formattedUrl) {
      case BASE_STORE_FINDER_URL:
        return cachedAriaLabels.constants.discover_store;
      case BASE_HOW_TO_CHANGE_STRAP_URL:
        return cachedAriaLabels.constants.learn_how_to_change_strap;
      case BASE_CAREER_URL:
        return cachedAriaLabels.constants.career;
      case BASE_PRESS_LOUNGE_URL:
        return cachedAriaLabels.constants.press_lounge;
      case BASE_MALLELONDON_URL:
        return cachedAriaLabels.constants.mallelondon_collection;
    }
  }

  const ariaLabel = cachedAriaLabels.urls[normalizedUrl];

  if (ariaLabel) {
    return ariaLabel;
  }

  // If no aria-label found in dictionary, generate a fallback label
  // This ensures links always have meaningful aria-labels
  return generateFallbackAriaLabel(normalizedUrl);
}

/**
 * Get action aria-label synchronously (uses cached values)
 */
export function getActionAriaLabelSync(
  actionKey: string,
  languageCode: string,
): string | undefined {
  if (!languageCode) {
    return undefined;
  }
  // Normalize the language code to match cache keys
  const normalizedLangCode = languageCode.toUpperCase().replace('-', '_');
  const cachedAriaLabels = ariaLabelCache.get(normalizedLangCode);
  if (!cachedAriaLabels) {
    return undefined;
  }
  return cachedAriaLabels.actions[actionKey];
}
